/**
 * Modal shell for the barcode-scanning dialogs (booking picker, return
 * prompt, "no booking found"). Same look as ChangeAllocationModal.
 */

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ScanLine } from 'lucide-react';

interface ScanModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  testId?: string;
}

export default function ScanModal({ title, subtitle, onClose, children, footer, testId }: ScanModalProps) {
  const { t } = useTranslation();

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 isolate" data-testid={testId}>
      <div
        className="rounded-lg shadow-2xl w-full overflow-hidden flex flex-col mx-auto relative z-50 bg-primary"
        style={{ maxHeight: '90vh', maxWidth: '560px' }}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between p-md sm:p-lg border-b border-primary bg-primary">
          <div className="flex items-center gap-md min-w-0">
            <ScanLine className="w-5 h-5 sm:w-6 sm:h-6 text-blue flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-primary truncate">{title}</h2>
              {subtitle && <p className="text-sm text-secondary">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="btn-icon flex-shrink-0" title={t('scan.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-md sm:p-lg">{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-md p-md sm:p-lg border-t border-primary bg-primary">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
